import { resources } from "@cleanbudget/i18n";

/**
 * The explanations of the admin's help (`helpTopic`, "uitleg"; spec admin-help, "De teksten"). Each
 * lives in its area's namespace under `help` (`price.json`, `help.quantityDiscount`) and is named
 * `<namespace>:<key>`: `price:quantityDiscount`. The name is typed, so a (?) for an explanation that
 * does not exist does not compile; `bun run help:check` covers what the types cannot see (terms,
 * length, explanations nothing opens).
 */

type Dutch = (typeof resources)["nl"];

/** The namespaces that hold explanations. */
type HelpNamespace = {
	[N in keyof Dutch]: Dutch[N] extends { readonly help: object } ? N : never;
}[keyof Dutch];

/** The name of an explanation: `price:quantityDiscount`. */
export type HelpTopicId = {
	[N in HelpNamespace]: Dutch[N] extends { readonly help: infer H } ? `${N}:${keyof H & string}` : never;
}[HelpNamespace];

export interface HelpChoice {
	readonly name: string;
	readonly text: string;
}

export interface HelpTopic {
	readonly id: HelpTopicId;
	readonly title: string;
	/** Where it is, as the menu names it: "Producten › Prijzen". */
	readonly area: string;
	/** What it is, in 1 to 3 sentences, at most 40 words. */
	readonly short: string;
	readonly choices: readonly HelpChoice[];
	/** "Let op": what happens without being seen. */
	readonly warning?: string;
	/** Other explanations: a term in the text links to them, and they are under "Zie ook". */
	readonly terms: readonly HelpTopicId[];
	/** "Ga naar dit scherm": where the setting is. */
	readonly goTo?: { readonly to: string; readonly label: string };
}

interface RawTopic {
	readonly title: string;
	readonly area: string;
	readonly short: string;
	readonly choices?: readonly HelpChoice[];
	readonly warning?: string;
	readonly terms?: readonly string[];
	readonly goTo?: { readonly to: string; readonly label: string };
}

/** Every explanation in `namespaces` (each namespace's `help`), in the order they are written. */
export function topicsFrom(namespaces: Readonly<Record<string, unknown>>): HelpTopic[] {
	const topics: HelpTopic[] = [];
	for (const [namespace, keys] of Object.entries(namespaces)) {
		const help = (keys as { help?: Record<string, RawTopic> }).help;
		if (!help) continue;
		for (const [key, raw] of Object.entries(help)) {
			topics.push({
				id: `${namespace}:${key}` as HelpTopicId,
				title: raw.title,
				area: raw.area,
				short: raw.short,
				choices: raw.choices ?? [],
				warning: raw.warning,
				terms: (raw.terms ?? []) as HelpTopicId[],
				goTo: raw.goTo,
			});
		}
	}
	return topics;
}

/** Every explanation of the admin. */
export const helpTopics: readonly HelpTopic[] = topicsFrom(resources.nl);

const byId = new Map(helpTopics.map((topic) => [topic.id, topic]));

/** The explanation named `id`; the types and the help check make sure it exists. */
export function helpTopic(id: HelpTopicId): HelpTopic {
	const topic = byId.get(id);
	if (!topic) throw new Error(`no explanation ${id}`);
	return topic;
}

/**
 * The explanations per menu item, for the overview: grouped by the first part of their `area`
 * ("Producten" of "Producten › Prijzen"), in the menu's order; a group the menu does not name comes
 * last. Within a group, the order they are written in.
 */
export function topicsByMenu(
	topics: readonly HelpTopic[],
	menu: readonly string[],
): { readonly menu: string; readonly topics: readonly HelpTopic[] }[] {
	const groups = new Map<string, HelpTopic[]>();
	for (const topic of topics) {
		const name = topic.area.split("›")[0]?.trim() ?? topic.area;
		groups.set(name, [...(groups.get(name) ?? []), topic]);
	}
	const rank = (name: string) => {
		const index = menu.indexOf(name);
		return index < 0 ? menu.length : index;
	};
	return [...groups.entries()]
		.sort(([a], [b]) => rank(a) - rank(b))
		.map(([name, members]) => ({ menu: name, topics: members }));
}
